import type { AIProvider, GenerateRequest, GenerateResult, ProviderInfo, StreamHandler, ToolCall, UsageStats } from '../types'
import { AiError } from '../types'
import { readJsonOrThrow, readSseData } from '../streaming'
import { fetchOrThrow } from '../network'
import type { ProviderConfig } from '@/types/settings'
import { TEST_PROMPT } from '../prompts'
import { track } from '@/composables/useEventTracker'

/**
 * Devuelve el parámetro de límite de tokens con el nombre correcto para el
 * modelo. OpenAI renombró `max_tokens` → `max_completion_tokens` en la
 * familia gpt-4o/o1/o3/o4 y gpt-4.1; el nombre viejo devuelve 400 en esos
 * modelos. Los modelos anteriores (gpt-3.5-turbo, gpt-4…) siguen usando
 * `max_tokens` y no reconocen el nuevo.
 */
function maxTokensParam(
  model: string,
  value: number,
): { max_tokens: number } | { max_completion_tokens: number } {
  // Familias que exigen el nuevo nombre `max_completion_tokens`:
  // gpt-4o*, gpt-4.1*, gpt-5*, o1*, o3*, o4* y variantes experimentales
  // (sufijo "-luna"). Los modelos anteriores (gpt-3.5-turbo, gpt-4 sin
  // sufijo) siguen usando `max_tokens`.
  const usesNewParam =
    /^gpt-4o/i.test(model) ||
    /^gpt-4\.1/i.test(model) ||
    /^gpt-5/i.test(model) ||
    /^o[134]/i.test(model) ||
    /-luna$/i.test(model)
  return usesNewParam
    ? { max_completion_tokens: value }
    : { max_tokens: value }
}

/**
 * ¿Soporta el modelo el parámetro `temperature`? Los modelos de razonamiento
 * (o1, o3, o4) y las variantes experimentales "-luna" solo aceptan el valor
 * por defecto (1); mandar `temperature` devuelve 400.
 */
function supportsTemperature(model: string): boolean {
  if (/^o[134]/i.test(model)) return false
  if (/-luna$/i.test(model)) return false
  return true
}

/**
 * ¿Es un modelo de razonamiento (o1/o3/o4, gpt-5*-luna)? Estos modelos no
 * permiten `tools` con el razonamiento activo (error "Function tools with
 * reasoningeffort are not supported..."). Cuando hay tools, hay que forzar
 * `reasoning_effort: 'none'` para que acepte las herramientas.
 */
function isReasoningModel(model: string): boolean {
  return /^o[134]/i.test(model) || /-luna$/i.test(model)
}

/**
 * Adapter para OpenAI (Chat Completions API).
 *
 * Usa el endpoint `/chat/completions` con `stream: true` para recibir
 * los tokens incrementalmente vía SSE. Cada chunk trae
 * `choices[0].delta.content`.
 */
export class OpenAIProvider implements AIProvider {
  readonly info: ProviderInfo

  constructor(private cfg: ProviderConfig) {
    this.info = { provider: 'openai', model: cfg.model }
  }

  private get headers(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.cfg.apiKey}`,
    }
  }

  private get endpoint(): string {
    return `${this.cfg.baseUrl.replace(/\/$/, '')}/chat/completions`
  }

  private buildBody(req: GenerateRequest, stream: boolean) {
    // Si hay conversación multi-turno (chat), se usa directamente como
    // historial. En caso contrario, se compone system+user como antes.
    // OpenAI admite rol `tool` para los resultados de function-calling, y
    // `tool_calls` en mensajes assistant: mapeamos ambos al formato nativo.
    const messages: unknown[] =
      req.messages ?? (() => {
        const m: { role: 'system' | 'user'; content: string }[] = []
        if (req.system) m.push({ role: 'system', content: req.system })
        m.push({ role: 'user', content: req.user })
        return m
      })()
    // Si el historial viene de ChatMessage[], normalizar al formato OpenAI
    // (assistant con tool_calls → {role, content, tool_calls}; tool →
    // {role:'tool', tool_call_id, content}).
    const openaiMessages = (req.messages
      ? req.messages.map((m) => {
          if (m.role === 'assistant' && m.toolCalls && m.toolCalls.length > 0) {
            return {
              role: 'assistant' as const,
              content: m.content || null,
              tool_calls: m.toolCalls.map((tc) => ({
                id: tc.id,
                type: 'function' as const,
                function: { name: tc.name, arguments: JSON.stringify(tc.arguments) },
              })),
            }
          }
          if (m.role === 'tool') {
            return {
              role: 'tool' as const,
              tool_call_id: m.toolCallId ?? '',
              content: m.content,
            }
          }
          return { role: m.role, content: m.content }
        })
      : messages)

    const body: Record<string, unknown> = {
      model: this.cfg.model,
      messages: openaiMessages,
      // OpenAI renombró max_tokens → max_completion_tokens en gpt-4o, o1/o3/o4
      // y gpt-4.1; el nombre viejo devuelve 400 en esos modelos.
      ...maxTokensParam(this.cfg.model, req.maxTokens ?? 1024),
      stream,
      stream_options: stream ? { include_usage: true } : undefined,
    }
    // `temperature` solo si el modelo lo admite (o1/o3/o4 y "-luna" no).
    if (supportsTemperature(this.cfg.model)) {
      body.temperature = req.temperature ?? 0.4
    }
    // Function-calling: si se pasan tools, se inyectan con su tool_choice.
    // OpenAI usa el formato `{type:'function', function:{...}}` tal cual.
    if (req.tools && req.tools.length > 0) {
      body.tools = req.tools
      body.tool_choice = req.toolChoice ?? 'auto'
      // Los modelos de razonamiento (o1/o3/o4, gpt-5*-luna) rechazan tools
      // con razonamiento activo ("Function tools with reasoningeffort are
      // not supported"). Lo apagamos para que acepte las herramientas.
      if (isReasoningModel(this.cfg.model)) {
        body.reasoning_effort = 'none'
      }
    } else if (req.toolChoice === 'none') {
      // 'none' es válido aunque no haya tools (fuerza texto).
      body.tool_choice = 'none'
    }
    return body
  }

  async stream(
    req: GenerateRequest,
    onToken: StreamHandler,
  ): Promise<GenerateResult> {
    this.requireApiKey()

    const response = await fetchOrThrow(this.endpoint, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(this.buildBody(req, true)),
      signal: req.signal,
    }, 'OpenAI')

    if (!response.ok) {
      await readJsonOrThrow(response) // lanza AiError con detalle
      throw new AiError('Respuesta inválida de OpenAI.', response.status)
    }

    let text = ''
    let usage: UsageStats = { tokensIn: null, tokensOut: null }
    // Acumulador de tool_calls: OpenAI los streamtea fragmentados por `index`.
    // Cada delta trae {index, id?, function:{name?, arguments?}} y hay que
    // ir concatenando `name` y `arguments` (string JSON parcial).
    const toolCallAccum = new Map<number, { id: string; name: string; args: string }>()

    for await (const data of readSseData(response, req.signal)) {
      let chunk: {
        choices?: {
          delta?: {
            content?: string
            reasoning_content?: string
            tool_calls?: {
              index: number
              id?: string
              function?: { name?: string; arguments?: string }
            }[]
          }
        }[]
        usage?: { prompt_tokens?: number; completion_tokens?: number }
      }
      try {
        chunk = JSON.parse(data)
      } catch {
        continue
      }
      const d = chunk.choices?.[0]?.delta
      // Modelos de razonamiento (o1/o3, gpt-5*-luna) emiten en
      // `reasoning_content`; los normales en `content`.
      const delta = d?.content ?? d?.reasoning_content
      if (delta) {
        text += delta
        onToken(delta)
      }
      // Acumular fragmentos de tool_calls por índice.
      if (d?.tool_calls) {
        for (const tc of d.tool_calls) {
          const existing = toolCallAccum.get(tc.index) ?? { id: '', name: '', args: '' }
          if (tc.id) existing.id = tc.id
          if (tc.function?.name) existing.name += tc.function.name
          if (tc.function?.arguments) existing.args += tc.function.arguments
          toolCallAccum.set(tc.index, existing)
        }
      }
      if (chunk.usage) {
        usage = {
          tokensIn: chunk.usage.prompt_tokens ?? null,
          tokensOut: chunk.usage.completion_tokens ?? null,
        }
      }
    }

    // Reconstruir ToolCall[] finales parseando el JSON de argumentos.
    const toolCalls: ToolCall[] = []
    for (const [, acc] of toolCallAccum) {
      if (!acc.name && !acc.args) continue
      let args: Record<string, unknown> = {}
      if (acc.args) {
        try {
          args = JSON.parse(acc.args)
        } catch {
          // Argumentos malformados (stream cortado): args vacíos.
          args = {}
        }
      }
      toolCalls.push({ id: acc.id, name: acc.name, arguments: args })
    }

    // Métrica global: tokens IA consumidos (sin contenido sensible).
    void track('ai_request', {
      provider: 'openai',
      model: this.cfg.model,
      tokens: (usage.tokensIn ?? 0) + (usage.tokensOut ?? 0),
      stream: true,
    })
    return { text, usage, toolCalls: toolCalls.length > 0 ? toolCalls : undefined }
  }

  async generate(req: GenerateRequest): Promise<GenerateResult> {
    this.requireApiKey()
    const response = await fetchOrThrow(this.endpoint, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(this.buildBody(req, false)),
      signal: req.signal,
    }, 'OpenAI')
    const data = await readJsonOrThrow<{
      choices: {
        message: {
          content: string
          tool_calls?: { id: string; function: { name: string; arguments: string } }[]
        }
      }[]
      usage?: { prompt_tokens?: number; completion_tokens?: number }
    }>(response)
    const usage = {
      tokensIn: data.usage?.prompt_tokens ?? null,
      tokensOut: data.usage?.completion_tokens ?? null,
    }
    // Métrica global: tokens IA consumidos (sin contenido sensible).
    void track('ai_request', {
      provider: 'openai',
      model: this.cfg.model,
      tokens: (usage.tokensIn ?? 0) + (usage.tokensOut ?? 0),
      stream: false,
    })
    // Parsear tool_calls del mensaje (no-streaming: vienen completos).
    const msg = data.choices[0]?.message
    let toolCalls: ToolCall[] | undefined
    if (msg?.tool_calls && msg.tool_calls.length > 0) {
      toolCalls = msg.tool_calls.map((tc) => {
        let args: Record<string, unknown> = {}
        try {
          args = JSON.parse(tc.function.arguments)
        } catch {
          args = {}
        }
        return { id: tc.id, name: tc.function.name, arguments: args }
      })
    }
    return {
      text: msg?.content ?? '',
      usage,
      toolCalls,
    }
  }

  async testConnection(): Promise<void> {
    this.requireApiKey()
    const result = await this.generate({
      user: TEST_PROMPT,
      maxTokens: 5,
      temperature: 0,
    })
    if (!result.text) throw new AiError('OpenAI devolvió una respuesta vacía.')
  }

  private requireApiKey() {
    if (!this.cfg.apiKey) {
      throw new AiError('Falta la API key de OpenAI. Configúrala en Ajustes.')
    }
    // Validación de formato: la clave debe empezar por "sk-". Si el
    // usuario tecleó otra cosa (ej. una contraseña por error), lo
    // detectamos aquí en vez de mandarlo a la API y recibir un error
    // críptico de OpenAI. Solo se aplica al endpoint oficial; si se
    // apunta baseUrl a un compatible propio, se omite la comprobación.
    const isOfficial = /^https?:\/\/(api\.openai\.com)/i.test(this.cfg.baseUrl)
    if (isOfficial && !this.cfg.apiKey.startsWith('sk-')) {
      throw new AiError(
        'La API key de OpenAI no tiene un formato válido (debería empezar por "sk-"). Revísala en Ajustes → IA.',
      )
    }
  }
}
